import _ from 'lodash'
import Dataset from 'scoreboard/datasets/model/dataset'
import ChartDesignerBreakdown from '../model/chart-designer-breakdown'
const { isCalendarPeriodBreakdownType } = ChartDesignerBreakdown
import isGeographicOrRollup from 'scoreboard/datasets/is-geographic-or-rollup'

/**
 * Returns the valid field IDs and visibleLinkedFieldIds that can be used for a
 * chart breakdown, based on the current state of the chart.
 *
 * @param {ChartDesigner} chart
 *
 * @param {Dataset} dataset
 *
 * @param {ChartDesignerBreakdown} [breakdown] - if provided, will include the
 * breakdown's current field/vlf in the list (useful for editing a breakdown)
 *
 * @returns {{visibleLinkedFieldIds: Number[], fieldIds: Number[]}}
 */
function validBreakdownFieldIds ({ chart, dataset, breakdown, dateFieldsOnly = false }) {
	let usedFieldIds = chart.usedDatasetFieldIds()

	const datasetFields = breakdown ? breakdown.get('datasetFields') : null
	let allowedFieldIds = dataset.combinedFields
		.filter(field => {
			const selectedField = datasetFields ? datasetFields.find(f => f.datasetFieldId == field.id) : null
			if (selectedField && (!dateFieldsOnly || isCalendarPeriodBreakdownType(field.dataType))) {
				// If a breakdown is provided, include its field if it is a valid
				// data type
				return true
			}

			if (!isValidUnderlyingFieldType({ chart, fieldId: field.id, dataset, dateFieldsOnly })) {
				return false
			}

			// Don't include fields that are already used in the chart
			return !_.includes(usedFieldIds.fieldIds, field.id)
		})
		.map(field => field.id)

	let allowedVisibleLinkedFieldIds = _.values(dataset.visibleLinkedFieldsById)
		.filter(vlf => {
			let vlfId = vlf.id
			if (datasetFields && datasetFields.find(f => f.datasetVisibleLinkedFieldId == vlfId)) {
				// If a breakdown is provided, include its VLF
				return true
			}

			if (vlf.fieldId == Dataset.TOTAL) {
				// Can't breakdown on "Number of X" field; needs to be a real field
				return false
			}

			if (!isValidUnderlyingFieldType({ chart, fieldId: vlf.fieldId, dataset, dateFieldsOnly })) {
				return false
			}

			// Don't include VLFs that are already used in the chart
			return !_.includes(usedFieldIds.visibleLinkedFieldIds, vlfId)
		})
		.map(vlf => vlf.id)

	return {
		fieldIds: allowedFieldIds,
		visibleLinkedFieldIds: allowedVisibleLinkedFieldIds
	}
}

function isValidUnderlyingFieldType({ chart, fieldId, dataset, dateFieldsOnly }) {
	const underlyingField = dataset.getUnderlyingField(fieldId)
	if (!isValidDataType({ chart, field: underlyingField })) {
		return false
	}

	if (dateFieldsOnly && !isCalendarPeriodBreakdownType(underlyingField.dataType)) {
		return false
	}

	return true
}

function isValidDataType({ chart, field }) {
	if (chart.hasScorecardItemSources()) {
		// Charts with scorecards on them can only breakdown by
		// calendar period, so only date fields can be used
		return isCalendarPeriodBreakdownType(field.dataType)
	}

	if (isGeographicOrRollup(field.dataType)) {
		// support for breakdown on rollup fields will be added by #36775
		return false
	}

	return true
}

export default validBreakdownFieldIds

validBreakdownFieldIds.__filename__ = 'charts/edit/valid-breakdown-field-ids.js'
